import { useGetSso } from 'features/auth/api/sso';
import { useShowGeneralErrorNotification } from 'hooks';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from 'shared/ui/Inputs';
import { HTTP_HEADERS, SSO_LOGIN_METHODS } from '../../../../consts';
import { AuthContainerLoginHeader } from '../../AuthContainerLoginHeader';
import { LoginOptionsDivider } from '../LoginOptionsDivider';
import { SsoLoginMethods } from '../SsoLoginMethods';
import { RedirectToSsoModal } from './RedirectToSsoModal';

type Props = {
  email: string;
  provider: keyof typeof SSO_LOGIN_METHODS;
  backButtonAction?: () => void;
};

const PROVIDER_DISPLAY_NAMES: Record<keyof typeof SSO_LOGIN_METHODS, string> = {
  google: 'Google',
  azure: 'Microsoft',
  okta: 'Okta',
};

export const LoginSSO = ({ email, provider, backButtonAction }: Props) => {
  const { t } = useTranslation(['common', 'user']);
  const showGeneralErrorNotification = useShowGeneralErrorNotification();
  const providerName = PROVIDER_DISPLAY_NAMES[provider];

  if (Object.values(SSO_LOGIN_METHODS).indexOf(provider) === -1) {
    throw new Error(
      `Invalid provider, expected: ${Object.keys(SSO_LOGIN_METHODS).join(
        ', ',
      )}, got: ${provider}`,
    );
  }

  const { refetch: login } = useGetSso(
    {
      headers: {
        [HTTP_HEADERS.xRedirectUrl]: window.location.href,
      },
      pathParams: {
        ssoDriver: provider,
      },
      // Email is already known in this component (passed from Login.tsx after the
      // user typed their identifier on ChooseLoginMethod). Forwarding it lets the
      // backend resolve a per-tenant AuthConfig (if any) and build the OAuth redirect
      // with the customer's own client_id instead of the global Pergamin app.
      queryParams: email ? { email } : undefined,
    },
    {
      enabled: false,
      onSuccess: redirectTo => {
        window.location.assign(redirectTo);
      },
      // Backend returns 422 when the route driver doesn't match the user's per-tenant SSO
      // (e.g. Acme uses Azure but request hit /sso/google) and for Okta with no resolvable
      // AuthConfig. Without this, the 5-second auto-redirect would just silently no-op and
      // the user would stare at a non-functional modal indefinitely.
      onError: () => {
        showGeneralErrorNotification();
      },
    },
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      login();
    }, 5000);

    return () => clearTimeout(timeout);
  }, [login]);

  return (
    <>
      <RedirectToSsoModal
        isOpen
        onClose={() => {}}
        providerName={providerName}
        onRedirectButtonClick={login}
      />
      <AuthContainerLoginHeader backButtonAction={backButtonAction} />
      <TextInput
        id="email"
        label={t('email', { ns: 'common' })}
        placeholder={t('emailPlaceholder', { ns: 'common' })}
        autoFocus
        value={email}
      />
      <LoginOptionsDivider />
      <SsoLoginMethods />
    </>
  );
};
